import { Button } from "@clockwise/design-system";
import { ArrowForward, Check } from "@clockwise/icons";
import { ModifyDiffSummary } from "@clockwise/schema";
import { ProposalAppliesTo } from "@clockwise/schema/v2";
import { useGatewayMutation } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import {
  EventType,
  useUpdateActiveEvent,
} from "@clockwise/web-commons/src/util/ActiveEventContext";
import React from "react";
import {
  OddProposal,
  useUpdateDefragProposal,
} from "../../chat-plus-calendar/DefragProposalContext";
import { useUpdateHoverEvent } from "../../chat-plus-calendar/util/HoverEventContext";
import { EventCardCollapsed } from "../../event-card/EventCardCollapsed";
import { EventCardAttendee } from "../../event-card/types";
import { ConfirmProposalDocument } from "../../reschedule-confirmation-modal/__generated__/ConfirmProposal.v2.generated";
import { RemoveTradeoffFromProposalDocument } from "../../tradeoffs/__generated__/RemoveTradeoffFromProposal.v2.generated";
import { eventToast } from "../../web-app-calendar/notifiation-event/EventToast";

export const OnDemandDefrag = ({ proposal }: { proposal: OddProposal["proposal"] }) => {
  const { removeDiffInOnDemandDefrag, cancelOnDemandDefragRequest } = useUpdateDefragProposal();
  const [removeTradeoffFromProposal] = useGatewayMutation(RemoveTradeoffFromProposalDocument);
  const updateActiveEvent = useUpdateActiveEvent();
  const updateHoverEvent = useUpdateHoverEvent();

  const [confirmProposal] = useGatewayMutation(ConfirmProposalDocument, {
    refetchQueries: ["CalendarEventsQuery"],
    awaitRefetchQueries: true,
  });

  const handleRemovalOfTradeoff = (diffId: string) => {
    if (!diffId) {
      return;
    }
    void removeTradeoffFromProposal({
      variables: { eventDiffId: diffId },
      onCompleted: () => {
        removeDiffInOnDemandDefrag(diffId);
      },
      onError: () => {
        eventToast.error({ title: "Failed to remove tradeoff", operation: "CANCEL" });
      },
    });
  };

  const handleSaveAndFix = async () => {
    if (!proposal?.id) return;

    try {
      const result = await confirmProposal({
        variables: {
          proposalId: proposal.id,
          appliesTo: ProposalAppliesTo.Instance,
        },
      });
      if (result.data?.confirmProposal) {
        eventToast.success({ title: "Event moved succesfully", operation: "RESCHEDULE" });
        cancelOnDemandDefragRequest();
      } else {
        throw new Error("Failed to confirm proposal");
      }
    } catch (error) {
      console.log(`Failed to confirm proposal: ${error.message}`, error);
    }
  };

  const handleExpand = (diff: ModifyDiffSummary) => {
    const calendarID = diff.attendees.proposalAttendees.find((a) => {
      return a.attendeePerson.isYou;
    })?.attendeePerson?.primaryCalendar;
    if (!diff.eventId || !calendarID) return;
    updateActiveEvent({
      externalEventId: diff.eventId,
      calendarId: calendarID,
      type: EventType.Event,
    });
    updateHoverEvent(null);
  };

  const diffs = (proposal?.diffBlocks?.[0]?.diffs as unknown) as ModifyDiffSummary[];

  return (
    <div className="cw-flex cw-flex-col cw-gap-2">
      <div>{proposal?.diffBlocks?.[0]?.text}</div>
      {diffs?.map((diff) => {
        const attendees = diff.attendees.proposalAttendees.map(
          (attendee) => attendee.attendeePerson,
        ) as EventCardAttendee[];
        return (
          <EventCardCollapsed
            key={diff.id}
            eventName={diff.title}
            onExpandCard={() => {
              handleExpand(diff);
            }}
            subTextIcon={ArrowForward}
            subText={diff?.action?.label || ""}
            onRemoveCard={() => {
              handleRemovalOfTradeoff(diff.id);
            }}
            attendeePeople={attendees}
          />
        );
      })}
      <div className="cw-flex cw-gap-2">
        <Button size="xsmall" sentiment="positive" onClick={handleSaveAndFix} startIcon={Check}>
          Confirm
        </Button>
        <Button size="xsmall" variant="outlined" onClick={cancelOnDemandDefragRequest}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
